import type { User } from '@/userManagement/domain/entities/User';
import { mapUserDtoToEntity } from '@/userManagement/application/mappers/userMapper';
import type { UserWireDto } from '@/userManagement/application/types/UserWireDto';
import { mapCrossUserPayloadToWireDto } from '@/userManagement/application/mappers/crossUserListItemMapper';
import type { UserCrudErpSyncFeedback } from '@/userManagement/application/utils/userCrudErpSyncFeedback';

export type UserMutationOutcome =
  | { kind: 'applied'; user: User; initialPassword?: string; erp?: UserCrudErpSyncFeedback }
  | { kind: 'pendingApproval'; approvalRequestId?: number; message?: string };

export type UserStatusMutationOutcome =
  | { kind: 'applied'; isActive: boolean; erp?: UserCrudErpSyncFeedback }
  | { kind: 'pendingApproval'; approvalRequestId?: number; message?: string };

export interface ParseUserStatusMutationOptions {
  /** When the BE returns `UserStatusResultDto` without `isActive`, infer from the mutation (deactivate vs reactivate). */
  appliedIsActive?: boolean;
}

function readRecord(data: unknown): Record<string, unknown> | null {
  if (data == null || typeof data !== 'object') return null;
  return data as Record<string, unknown>;
}

function readApprovalRequestId(o: Record<string, unknown>): number | undefined {
  const raw = o.approvalRequestId ?? o.ApprovalRequestId ?? o.requestId ?? o.RequestId;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function readPendingFlag(o: Record<string, unknown>): boolean {
  const pending = o.pendingApproval ?? o.PendingApproval ?? o.isPendingApproval ?? o.IsPendingApproval;
  if (pending === true) return true;
  const status = String(o.status ?? o.Status ?? '').trim();
  return status === 'PendingReview' || status === 'PendingIT' || status === 'PendingApproval';
}

function readMessage(o: Record<string, unknown>): string | undefined {
  const msg = o.message ?? o.Message;
  return typeof msg === 'string' && msg.trim() !== '' ? msg.trim() : undefined;
}

function readIsActive(o: Record<string, unknown>): boolean | undefined {
  const raw = o.isActive ?? o.IsActive;
  if (typeof raw === 'boolean') return raw;
  return undefined;
}

function readInitialPassword(o: Record<string, unknown>): string | undefined {
  const raw = o.initialPassword ?? o.InitialPassword;
  return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : undefined;
}

function readNullableBoolean(o: Record<string, unknown>, ...keys: string[]): boolean | null | undefined {
  for (const key of keys) {
    const raw = o[key];
    if (typeof raw === 'boolean') return raw;
  }
  return undefined;
}

function readNullableString(o: Record<string, unknown>, ...keys: string[]): string | null | undefined {
  for (const key of keys) {
    const raw = o[key];
    if (typeof raw === 'string') return raw.trim() !== '' ? raw.trim() : null;
  }
  return undefined;
}

function readErpSyncFeedback(o: Record<string, unknown>): UserCrudErpSyncFeedback {
  return {
    erpApplyPending: readNullableBoolean(o, 'erpApplyPending', 'ErpApplyPending'),
    erpSyncWarning: readNullableString(o, 'erpSyncWarning', 'ErpSyncWarning'),
    erpSynced: readNullableBoolean(o, 'erpSynced', 'ErpSynced')
  };
}

function readStringArray(o: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const raw = o[key];
    if (Array.isArray(raw)) {
      return raw.map((v) => String(v)).filter((v) => v.trim() !== '');
    }
  }
  return [];
}

function mapCreateUserResultToUser(o: Record<string, unknown>): User | null {
  const username = String(o.username ?? o.Username ?? '').trim();
  const crossUserIds = readStringArray(o, 'crossUserIds', 'CrossUserIds');
  const databases = readStringArray(o, 'databases', 'Databases');
  const id = crossUserIds[0] ?? '';
  if (username === '' && id === '') return null;

  return {
    id,
    username,
    firstName: '',
    lastName: '',
    email: '',
    phone: null,
    permissionRoleId: null,
    permissionRoleName: null,
    isLocked: false,
    isSuspended: false,
    isActive: true,
    forceNewPassword: false,
    failedLoginAttempts: 0,
    lastLoginOn: null,
    assignedDatabases: databases,
    utcUpdatedOn: new Date().toISOString()
  };
}

function mapWireToUser(data: unknown): User | null {
  const wire = mapCrossUserPayloadToWireDto(data) ?? (data as UserWireDto | null);
  if (wire == null) return null;
  try {
    return mapUserDtoToEntity(wire);
  } catch {
    return null;
  }
}

function toPendingApprovalOutcome(o: Record<string, unknown>) {
  return {
    kind: 'pendingApproval' as const,
    approvalRequestId: readApprovalRequestId(o),
    message: readMessage(o)
  };
}

/**
 * Parses create / update-profile responses — applied user vs pending-approval result.
 *
 * TROC-198 OpenAPI documents **200** (applied) and **202** (submitted for approval) for
 * POST /users and PUT /users/{id}/profile. Pending approval is detected from HTTP **202**
 * and/or response body flags (`pendingApproval`, `approvalRequestId`, `status`, etc.).
 */
export function parseUserMutationOutcome(data: unknown, httpStatus?: number): UserMutationOutcome {
  const o = readRecord(data);
  if (o != null && httpStatus === 202) {
    return toPendingApprovalOutcome(o);
  }
  if (o != null && readPendingFlag(o) && readApprovalRequestId(o) != null) {
    return toPendingApprovalOutcome(o);
  }

  const user =
    (o != null && (o.crossUserIds != null || o.CrossUserIds != null) ? mapCreateUserResultToUser(o) : null) ?? mapWireToUser(data);
  if (user != null) {
    const initialPassword = o != null ? readInitialPassword(o) : undefined;
    const erp = o != null ? readErpSyncFeedback(o) : undefined;
    return { kind: 'applied', user, initialPassword, erp };
  }

  if (o != null && readPendingFlag(o)) {
    return toPendingApprovalOutcome(o);
  }

  throw new Error('Unrecognized user mutation response from User Management API.');
}

/**
 * Parses deactivate / reactivate responses.
 *
 * Same contract as {@link parseUserMutationOutcome}: OpenAPI documents 200 (applied) and 202
 * (submitted for approval) plus body flags.
 */
export function parseUserStatusMutationOutcome(
  data: unknown,
  httpStatus?: number,
  options?: ParseUserStatusMutationOptions
): UserStatusMutationOutcome {
  const o = readRecord(data);
  if (o != null && httpStatus === 202) {
    return toPendingApprovalOutcome(o);
  }
  if (o != null && (readPendingFlag(o) || readApprovalRequestId(o) != null)) {
    const isActive = readIsActive(o);
    if (isActive === undefined && !readPendingFlag(o)) {
      const appliedIsActive = options?.appliedIsActive;
      if (appliedIsActive !== undefined) {
        return { kind: 'applied', isActive: appliedIsActive, erp: readErpSyncFeedback(o) };
      }
      return { kind: 'applied', isActive: false, erp: readErpSyncFeedback(o) };
    }
    if (readPendingFlag(o) || readApprovalRequestId(o) != null) {
      return toPendingApprovalOutcome(o);
    }
  }

  const erp = o != null ? readErpSyncFeedback(o) : undefined;
  const isActive = o != null ? readIsActive(o) : undefined;
  if (isActive !== undefined) {
    return { kind: 'applied', isActive, erp };
  }

  if (httpStatus === 200 || httpStatus === 201) {
    const appliedIsActive = options?.appliedIsActive;
    if (appliedIsActive !== undefined) {
      return { kind: 'applied', isActive: appliedIsActive, erp };
    }
    return { kind: 'applied', isActive: false, erp };
  }

  throw new Error('Unrecognized user status mutation response from User Management API.');
}

export const USER_CRUD_PENDING_APPROVAL_SNACKBAR = {
  title: 'Submitted for approval',
  message: 'Your request has been sent to IT for approval.'
} as const;
